// Labels: a DMA end, a firmware command's arguments, a core's name. The host and DMA
// phrasing is ported from symtpu/tools/build_trace_viewer.py (end_str, host_args).

import { bytes } from "./dom.js";

export const MEM_WORD = {
  HostMemory: "host memory", Hbm: "HBM", Vmem: "VMEM", Smem: "SMEM", Imem: "IMEM",
  Cmem: "CMEM", Spmem: "SPMEM", TileSpmem: "tile SPMEM", Timem: "TIMEM",
};

export function coreShort(core, ci, tile) {
  const k = core === "TensorCore" ? "TC" : core === "SparseCore" ? "SC" : core === "HostInterface" ? "host-if"
    : core === "MonCore" ? "mon" : core == null ? "?" : String(core);
  let s = k + (ci == null ? "" : ci);
  if (tile != null) s += " tile " + tile;
  return s;
}

// "HBM" / "VMEM of TC1" / "SMEM of SC2"; with the chip when `withChip`.
export function endStr(end, withChip) {
  if (!end) return "?";
  const core = end.core;
  let where = MEM_WORD[end.mem] || String(end.mem);
  if (core && core !== "MonCore" && core !== "None" && !["Hbm", "HostMemory"].includes(end.mem)) {
    where += " of " + coreShort(core, end.ci);
  }
  if (withChip && end.chip != null) where = "chip " + end.chip + " " + where;
  return where;
}

export function dmaShort(d) {
  const r = d.runs && d.runs[0];
  if (!r) return bytes(d.bytes);
  const cross = r.src && r.dst && r.src.chip !== r.dst.chip;
  return bytes(d.bytes) + " " + endStr(r.src, cross) + " -> " + endStr(r.dst, cross) + (d.runs.length > 1 ? " x" + d.runs.length : "");
}

export function issuer(by) {
  if (by == null) return "?";
  if (by === "Host") return "the host";
  const m = /^(TensorCore|SparseCore)\((\d+)\)$/.exec(by);
  return m ? coreShort(m[1], +m[2]) : String(by);
}

// One line of arguments for a firmware command, in the command's own terms.
export function hostArgs(e) {
  const k = e.kind;
  const core = () => coreShort(e.core, e.ci);
  if (k === "dma") {
    return bytes(e.bytes || 0) + " " + endStr(e.src) + " -> " + endStr(e.dst)
      + (e.type == null || e.type === "Local" ? "" : "  " + e.type + (e.chip_to != null ? " chip " + e.chip_to : ""));
  }
  if (k === "run") {
    if (e.op === "control_tile") return `${e.op}  ${core()}  flags ${e.flags}  tiles 0x${(e.tile_mask || 0).toString(16)}`;
    return `${e.op}  ${core()}  pc ${e.pc}`;
  }
  if (k === "sync_flag") {
    return `${e.op}  flag ${e.idx} on ${core()}${e.tile != null ? " tile " + e.tile : ""}${e.v == null ? "" : "  value " + e.v}`;
  }
  if (k === "throttle") {
    return `${e.op}  ${e.core == null ? "chip" : core()}${e.level == null ? "" : "  level " + e.level}`;
  }
  if (k === "status") return core();
  if (k === "profiler") return `op ${e.op}  param ${e.param}`;
  if (k === "core_dump") return `${e.req}  ${e.op}`;
  if (k === "vf_attributes") return String(e.attr);
  if (k === "global_chip_id") return `${e.op}${e.v == null ? "" : "  = " + e.v}`;
  if (k === "power_management") return `${e.op}  ${e.arg}  ${e.state}`;
  if (k === "error_control") return String(e.op);
  return "";
}

// The class a host command is coloured by.
export function hostClass(kind) {
  return ["run", "dma", "sync_flag", "throttle", "status"].includes(kind) ? kind : "other";
}

export function hostLine(h) {
  if (h.ev === "doorbell") return `doorbell  published ${h.tail - h.prev} command${h.tail - h.prev === 1 ? "" : "s"} (tail ${h.prev} -> ${h.tail})`;
  if (h.ev === "irq") return `irq  ${h.kind || "interrupt"}${h.n != null ? " x" + h.n : ""}${h.core ? " from " + h.core : ""}`;
  if (h.ev === "fw_done") return `done #${h.id}  ${h.status}${h.err ? " " + h.err : ""}`;
  return `${h.kind} #${h.id}  ${hostArgs(h)}`;
}

// Axis ticks: 1/2/5 x 10^k picoseconds, at least `minPx` apart.
export function tickStep(psPerPx, minPx = 80) {
  const target = psPerPx * minPx;
  const p = Math.pow(10, Math.floor(Math.log10(target)));
  for (const m of [1, 2, 5, 10]) if (m * p >= target) return m * p;
  return 10 * p;
}

// A SparseCore slot in the decoder's own syntax (`MoveY d2 x13 y#0x1bfb p7/0`,
// `vst op=3 s2 ix0 m16 ...`), rendered the way one would write it. The decoder's
// mnemonics are kept where they are already words; the encodings that are only
// numbers get names.
const SC_ALU = {
  MoveY: "mov", IntegerAdd: "add", IntegerSubtractYX: "sub", Multiply32BitIntegers: "mul",
  BitwiseAnd: "and", BitwiseOr: "or", BitwiseXor: "xor", LogicalShiftLeftXByYPlaces: "shl",
  LogicalShiftRightXByYPlaces: "shr", ArithmeticShiftRightXByYPlaces: "sar",
  CompareIntegerEq: "cmp.eq", CompareIntegerNe: "cmp.ne", CompareSignedIntegerGt: "cmp.gt",
  CompareSignedIntegerLt: "cmp.lt", CompareUnsignedIntegerLt: "cmp.ltu", CompareUnsignedIntegerGt: "cmp.gtu",
  BranchRelative: "br", Delay: "delay", Halt: "halt", PopV2s: "pop v→s", SetTag: "tag",
  ReadRegisterTileid: "tileid", ReadRegisterSparseCoreId: "scid", ReadDreg: "dreg",
  ScalarLoadSmemY: "sld", ScalarStoreXToSmemY: "sst", ScalarFenceSelect: "fence",
  SyncGreaterOrEqual: "wait flag ≥", SyncGreaterOrEqualDone: "wait done", SetSyncDone: "flag.done =",
  AddSyncFlag: "flag +=", SyncBarrier: "barrier", AtomicTileAdd: "atomic.add", Trace: "trace",
  ReadSyncFlag: "flag.read", SetSyncFlag: "flag =", Sfence: "sfence",
};
const SC_VST = { 0: "vst", 3: "vst.add", 1: "vst.masked", 2: "vst.strided" };
const SC_VLD = { 0: "vld", 1: "vld.masked", 2: "vld.strided" };
export function scSlotText(unit, text) {
  let m;
  if (unit === "alu" || unit === "misc") {
    m = /^(\w+)\s*(.*)$/.exec(text);
    if (m && SC_ALU[m[1]]) return `${SC_ALU[m[1]]} ${m[2]}`;
    return text;
  }
  if (unit === "vst" || unit === "vld") {
    m = /^op=(\d+)\s*(.*)$/.exec(text);
    if (m) return `${(unit === "vst" ? SC_VST : SC_VLD)[m[1]] || unit + "." + m[1]} ${m[2]}`;
    return text;
  }
  if (unit === "valu") { m = /^op=(\d+)\s*(.*)$/.exec(text); return m ? `valu.${m[1]} ${m[2]}` : text; }
  if (unit === "stream") {
    m = /^Linear op(\d+)\s*(.*)$/.exec(text);
    if (m) return `${{ 0: "stream in", 4: "stream out" }[m[1]] || "stream." + m[1]} ${m[2]}`;
    return text;
  }
  if (unit === "dma") return "dma " + text;
  return text;
}

// A slot's text as shown: the TensorCore listing line as is, a SparseCore slot humanised.
export function slotText(kind, u, i) {
  return kind === "tc" ? i : scSlotText(u.replace(/\d+$/, ""), i);
}

// A slot's text as `mnemonic` and `operands`, the way a listing aligns them. The
// simulator's text is not uniform (some mnemonics are padded to a column, vector
// slots repeat their own slot name in front), so this normalises: the slot-name
// prefix goes, runs of spaces collapse, and the first word is the mnemonic.
export function splitSlot(kind, u, i, slot = null) {
  // From a trace the parts are already separate: the mnemonic as stored, the
  // operands joined the way the writer's own text joins them. Only the SparseCore
  // humanising still applies to the mnemonic.
  if (slot && slot.mn != null) {
    const ops = slot.ops.join(", ");
    if (kind === "tc") return { mn: slot.mn, ops };
    const h = scSlotText(u.replace(/\d+$/, ""), slot.mn + (ops ? " " + ops : ""));
    const sp = h.indexOf(" ");
    return sp < 0 ? { mn: h, ops: "" } : { mn: h.slice(0, sp), ops: h.slice(sp + 1) };
  }
  let text = slotText(kind, u, i);
  if (text.startsWith(u + " ")) text = text.slice(u.length + 1);
  text = text.replace(/\s+/g, " ").trim();
  const sp = text.indexOf(" ");
  return sp < 0 ? { mn: text, ops: "" } : { mn: text.slice(0, sp), ops: text.slice(sp + 1) };
}

// The slot columns of a listing, in the order a reader expects them: scalar, vector
// ALUs, extended, results, loads, stores, then the rest; names the table does not
// know go last, alphabetically.
const TC_ORDER = ["s0", "s1", "s2", "valu0", "valu1", "valu2", "valu3", "vex0", "vex1", "vres0", "vres1", "vld0", "vld1", "vld2", "vst0", "vst1", "vst2", "dma", "sync", "vmsk", "misc"];
const SC_ORDER = ["alu0", "alu1", "misc", "dma", "stream", "vld", "vst", "valu"];
export function slotColumns(kind, names) {
  const order = kind === "tc" ? TC_ORDER : SC_ORDER;
  const known = order.filter((n) => names.has(n));
  const rest = [...names].filter((n) => !order.includes(n)).sort();
  return known.concat(rest);
}

// One sync flag's identity as a number: whose flag file it is (chip, core kind, core
// index, tile) and the index within it. A number and not a string because a run has a
// million flag writes and this is what indexes them; the hardware's own bounds -- a
// core index under 31, a tile under 32, an index under a million -- make it exact.
export function flagIdOf(chip, core, ci, tile, idx) {
  const kind = core === "TensorCore" ? 0 : core === "SparseCore" ? 1 : core === "HostInterface" ? 2 : 3;
  return (((chip * 8 + kind) * 32 + (ci == null ? 31 : ci)) * 33 + (tile == null ? 0 : tile + 1)) * 1048576 + idx;
}

export function flagLabel(chip, core, ci, tile, idx) {
  return `flag ${idx} of chip ${chip} ${coreShort(core, ci, tile)}`;
}
